import * as monthUtils from '@actual-app/core/shared/months';
import type { CategoryGroupEntity } from '@actual-app/core/types/models';

import {
  useCreateCategoryGroupMutation,
  useMoveCategoryMutation,
  useSaveCategoryGroupMutation,
  useSaveCategoryMutation,
} from '#budget';
import { pushModal } from '#modals/modalsSlice';
import { useDispatch } from '#redux';

import { HojaNuevaCategoria } from './HojaNuevaCategoria';
import { planCambioTipo } from './travesias';
import { usePagadas } from './usePagadas';
import { useTravesiaCategoria } from './useTravesiaCategoria';

type HojaEditarCategoriaProps = {
  /** Categoría o grupo que se edita. */
  id: string;
  grupos: CategoryGroupEntity[];
  onCerrar: () => void;
  /** Justo antes de guardar (en «Editar categorías», para animar la lista). */
  antesDeGuardar?: () => void;
  /** La categoría cambia de grupo (orden provisional hasta que llegue el real). */
  onMovida?: (categoriaId: string, grupoId: string) => void;
  /**
   * La ayuda de Gavi la enseña abierta: sin teclado y «Guardar» no hace
   * nada (Plan, «mantén pulsada una categoría»).
   */
  ejemplo?: boolean;
};

/**
 * «Edita la categoría» / «Edita el grupo»: icono, nombre, grupo y tipo, y
 * «Guardar». La misma hoja en «Editar categorías» (tocar un nombre) y en el
 * Plan (mantener pulsada una categoría o un grupo, Vadik 31-oct-2026).
 */
export function HojaEditarCategoria({
  id,
  grupos,
  onCerrar,
  antesDeGuardar,
  onMovida,
  ejemplo,
}: HojaEditarCategoriaProps) {
  const createCategoryGroup = useCreateCategoryGroupMutation();
  const saveCategory = useSaveCategoryMutation();
  const saveCategoryGroup = useSaveCategoryGroupMutation();
  const moveCategory = useMoveCategoryMutation();
  const dispatch = useDispatch();
  const { setFijo } = usePagadas(monthUtils.currentMonth());

  const grupo =
    grupos.find(g => g.id === id) ??
    grupos.find(g => g.categories?.some(c => c.id === id)) ??
    null;
  const categoria = grupo?.categories?.find(c => c.id === id) ?? null;
  const travesia = useTravesiaCategoria(categoria);

  if (!grupo) {
    return null;
  }

  const onCrearGrupo = async (nombre: string): Promise<string | null> => {
    try {
      const nuevo = await createCategoryGroup.mutateAsync({ name: nombre });
      return nuevo ?? null;
    } catch {
      return null;
    }
  };

  const abrirHoja = (name: 'zarpar' | 'cerrar-travesia', categoryId: string) =>
    dispatch(
      pushModal({
        modal: {
          name,
          options: { categoryId, month: monthUtils.currentMonth() },
        },
      }),
    );

  return (
    <HojaNuevaCategoria
      modo={categoria ? 'categoria' : 'grupo'}
      editar={{ nombre: categoria?.name ?? grupo.name }}
      grupos={grupos.filter(
        g =>
          !!g.is_income === !!grupo.is_income &&
          (!g.hidden || g.id === grupo.id),
      )}
      grupoInicial={grupo.id}
      tipo={categoria && !categoria.is_income ? travesia.tipo : undefined}
      ejemplo={
        ejemplo
          ? {
              nombre: categoria?.name ?? grupo.name,
              grupoId: grupo.id,
              eligiendo: false,
            }
          : undefined
      }
      onCrear={(nombre, nuevoGrupo, tipo) => {
        if (ejemplo) {
          return;
        }
        onCerrar();
        antesDeGuardar?.();
        if (!categoria) {
          if (nombre !== grupo.name) {
            saveCategoryGroup.mutate({ group: { ...grupo, name: nombre } });
          }
          return;
        }
        if (tipo) {
          const plan = planCambioTipo(travesia.tipo, tipo);
          if (plan.fijo != null) {
            setFijo(categoria.id, plan.fijo);
          }
          if (plan.zarpar) {
            abrirHoja('zarpar', categoria.id);
          } else if (plan.cerrar) {
            abrirHoja('cerrar-travesia', categoria.id);
          }
        }
        if (nombre !== categoria.name) {
          saveCategory.mutate({ category: { ...categoria, name: nombre } });
        }
        if (nuevoGrupo && nuevoGrupo.id !== grupo.id) {
          onMovida?.(categoria.id, nuevoGrupo.id);
          moveCategory.mutate({
            id: categoria.id,
            groupId: nuevoGrupo.id,
            targetId: null,
          });
        }
      }}
      onCrearGrupo={grupo.is_income ? undefined : onCrearGrupo}
      onCerrar={onCerrar}
    />
  );
}
